import * as React from 'react';
import { useEffect, useRef } from 'react';
import { Check, ChevronRight, Search, Split } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { SubtleLink } from '@cotera/client/app/components/ui/link';
import { Icon } from '../ui/icon';
import type {
  ResourcePickerCategory,
  ResourcePickerItem,
  VisibleRow,
} from './types';
import type { MentionOptionDetail } from '../ui/tiptap/extensions';
import { CATEGORY_ICON_COLOR } from '@cotera/client/app/v1/resources/type/resource-type-colors';

// ─── Category icon colors (matching mention pill / resource-type colors) ──

const CATEGORY_ICON_NAME: Record<ResourcePickerCategory, React.ReactNode> = {
  agent: <Icon icon="bot" />,
  document: <Icon icon="document-text" />,
  folder: <Icon icon="folder" />,
  trigger: <Icon icon="clock" />,
  dataset: <Icon icon="dataset" />,
  tool: <Icon icon="wrench" />,
  workflow: <Icon icon="workflow" />,
  input: <Icon icon="variable" />,
  // Builtin steps have no shared glyph in the Icon set; each item supplies its
  // own (Repeat/Filter/Megaphone), so this is only the fallback.
  'flow-control': <Split />,
};

/** Render a colored category icon for a picker item. */
const CategoryIcon: React.FC<{
  category: ResourcePickerCategory;
  className?: string;
}> = ({ category, className }) => (
  <span
    className={cn(
      'flex size-4 shrink-0 items-center justify-center [&_svg]:size-4',
      CATEGORY_ICON_COLOR[category],
      className
    )}
  >
    {CATEGORY_ICON_NAME[category]}
  </span>
);

/** Resolve the icon for a picker item: prefer item.icon (provider logos), fall back to colored category icon. */
const ItemIcon: React.FC<{ item: ResourcePickerItem }> = ({ item }) => {
  if (item.icon !== undefined) {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-4">
        {item.icon}
      </span>
    );
  }
  // Tinted per type: every category previously drew the same grey glyph, so a
  // column of them carried no information at a glance.
  return (
    <span className={cn('flex shrink-0', CATEGORY_ICON_COLOR[item.category])}>
      <CategoryIcon category={item.category} />
    </span>
  );
};

// ─── Scroll helper ──────────────────────────────────────────────────

const scrollIntoNearestScrollParent = (el: HTMLElement): void => {
  let scrollParent: HTMLElement | null = el.parentElement;
  while (scrollParent !== null) {
    if (scrollParent.scrollHeight > scrollParent.clientHeight) {
      break;
    }
    scrollParent = scrollParent.parentElement;
  }
  if (scrollParent === null) {
    return;
  }
  const elRect = el.getBoundingClientRect();
  const parentRect = scrollParent.getBoundingClientRect();
  if (elRect.bottom > parentRect.bottom) {
    scrollParent.scrollTop += elRect.bottom - parentRect.bottom;
  } else if (elRect.top < parentRect.top) {
    scrollParent.scrollTop -= parentRect.top - elRect.top;
  }
};

// ─── Group header ───────────────────────────────────────────────────

export const ResourcePickerGroupHeader: React.FC<{
  label: string;
  icon?: React.ReactNode;
  category?: ResourcePickerCategory;
}> = ({ label }) => (
  <div className="flex items-center gap-2 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground first:pt-2">
    {label}
  </div>
);

/** Leaves the picker for the full catalogue. */
export const ResourcePickerBrowseAllRow: React.FC<{
  label: string;
  rowId?: string;
  isCursored: boolean;
  onSelect: () => void;
  onHover: () => void;
}> = ({ label, rowId, isCursored, onSelect, onHover }) => (
  <div
    role="option"
    id={rowId}
    aria-selected={isCursored}
    className={cn(
      'mt-1 flex cursor-pointer items-center gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground transition-colors',
      isCursored
        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
        : 'hover:bg-sidebar-accent'
    )}
    onClick={onSelect}
    onMouseEnter={onHover}
  >
    <Search className="size-3.5 shrink-0" />
    <span>{label}</span>
  </div>
);

/** Opens a capped section in place. Navigable, so the keyboard reaches it. */
export const ResourcePickerShowMoreRow: React.FC<{
  label: string;
  rowId?: string;
  isCursored: boolean;
  onExpand: () => void;
  onHover: () => void;
}> = ({ label, rowId, isCursored, onExpand, onHover }) => (
  <div
    role="option"
    id={rowId}
    aria-selected={isCursored}
    className={cn(
      'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground transition-colors',
      isCursored
        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
        : 'hover:bg-sidebar-accent'
    )}
    onClick={onExpand}
    onMouseEnter={onHover}
  >
    <ChevronRight className="size-3.5 shrink-0 rotate-90" />
    <span>{label}</span>
  </div>
);

// ─── Provider trigger row ───────────────────────────────────────────

export const ResourcePickerProviderRow: React.FC<{
  row: Extract<VisibleRow, { kind: 'provider-trigger' }>;
  rowId?: string;
  isCursored: boolean;
  onToggle: (provider: string) => void;
  onHover: (navIndex: number) => void;
}> = ({ row, rowId, isCursored, onToggle, onHover }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCursored && ref.current !== null) {
      scrollIntoNearestScrollParent(ref.current);
    }
  }, [isCursored]);

  return (
    <div
      ref={ref}
      role="option"
      id={rowId}
      aria-selected={isCursored}
      // `option` does not support `aria-expanded`, so the collapsed/expanded
      // state rides in the accessible name instead of an unsupported attribute.
      aria-label={`${row.label}, ${row.toolCount} ${
        row.toolCount === 1 ? 'tool' : 'tools'
      }, ${row.expanded ? 'expanded' : 'collapsed'}`}
      className={cn(
        'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors',
        isCursored
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'hover:bg-sidebar-accent'
      )}
      onClick={() => onToggle(row.provider)}
      onMouseEnter={() => onHover(row.navIndex)}
    >
      {row.icon !== undefined ? (
        <span className="flex size-5 shrink-0 items-center justify-center [&>*]:!size-5">
          {row.icon}
        </span>
      ) : (
        <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4">
          <Icon icon="wrench" />
        </span>
      )}
      <span className="flex-1 truncate font-medium text-sm">{row.label}</span>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {row.someAvailable && (
          <span className="inline-block size-2 rounded-full bg-emerald-500" />
        )}
        {row.toolCount} {row.toolCount === 1 ? 'tool' : 'tools'}
      </span>
      {/* Trailing, like the disclosure on every other category's rows. Leading
          it here made expanding a provider look like a different gesture from
          expanding a document. */}
      <ChevronRight
        className={cn(
          'size-3.5 shrink-0 text-muted-foreground transition-transform duration-150',
          row.expanded && 'rotate-90'
        )}
      />
    </div>
  );
};

// ─── Inline detail panel ────────────────────────────────────────────

const ItemDetailPanel: React.FC<{
  detail: MentionOptionDetail;
  indented: boolean;
  onDismiss?: () => void;
}> = ({ detail, indented, onDismiss }) => (
  /*
   * Deliberately terse. This sits inside a popover over a live composer, so a
   * page-sized panel pushes the rest of the list out of view: at the previous
   * 16px rhythm one open detail cost roughly 200px and left room for three
   * tools.
   *
   * Access is not repeated here. Write tools are flagged on the row itself,
   * and restating access with a coloured dot also gave dots two meanings in
   * one list, since an emerald dot on the provider row means "connected".
   */
  <div
    className={cn(
      'px-4 pb-2.5 pt-0.5 text-muted-foreground',
      indented && 'ml-10 border-l-2 border-border/50'
    )}
  >
    {/* Clamped as well as shortened at the source: descriptions are written by
        tool authors for a model, so the panel must not be able to grow without
        bound whatever arrives. `Open details` reaches the full text. */}
    {detail.summary !== undefined && (
      <p className="mb-1.5 line-clamp-3 text-[12.5px] leading-snug text-muted-foreground/90">
        {detail.summary}
      </p>
    )}
    {detail.onOpen !== undefined && (
      <SubtleLink
        className="text-xs text-muted-foreground"
        onClick={(e) => {
          e.stopPropagation();
          detail.onOpen?.();
          onDismiss?.();
        }}
      >
        Open details
      </SubtleLink>
    )}
  </div>
);

// ─── Item row ───────────────────────────────────────────────────────

export const ResourcePickerItemRow: React.FC<{
  item: ResourcePickerItem;
  isCursored: boolean;
  isSelected: boolean;
  rowId?: string;
  indented: boolean;
  showTypeLabel?: boolean;
  forceExpanded: boolean;
  multiSelect: boolean;
  onSelect: (item: ResourcePickerItem, event: React.MouseEvent) => void;
  onToggleSelection: (id: string) => void;
  onToggleDetail: (id: string) => void;
  onHover: (navIndex: number) => void;
  onDismiss?: () => void;
  navIndex: number;
}> = ({
  item,
  isCursored,
  isSelected,
  rowId,
  indented,
  showTypeLabel,
  forceExpanded,
  multiSelect,
  onSelect,
  onToggleSelection,
  onToggleDetail,
  onHover,
  onDismiss,
  navIndex,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCursored && ref.current !== null) {
      scrollIntoNearestScrollParent(ref.current);
    }
  }, [isCursored]);

  const handleClick = (event: React.MouseEvent) => {
    if (multiSelect) {
      onToggleSelection(item.id);
    } else {
      onSelect(item, event);
    }
  };

  const hasDetail = item.detail !== undefined;
  const isExpanded = hasDetail && forceExpanded;
  const accessLabel = item.detail?.access;

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleDetail(item.id);
  };

  return (
    <div ref={ref}>
      <div
        role="option"
        id={rowId}
        aria-selected={isCursored}
        aria-checked={multiSelect ? isSelected : undefined}
        className={cn(
          'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors',
          indented && 'pl-10',
          isCursored
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'hover:bg-sidebar-accent'
        )}
        onClick={handleClick}
        onMouseEnter={() => onHover(navIndex)}
      >
        {multiSelect && (
          <span
            className={cn(
              'flex size-4 shrink-0 items-center justify-center rounded border',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border'
            )}
          >
            {isSelected && <Check className="size-3" />}
          </span>
        )}
        <ItemIcon item={item} />
        <span className={cn('flex-1 truncate', indented && 'text-[13px]')}>
          {item.label}
        </span>
        {showTypeLabel === true && item.typeLabel !== undefined && (
          <span className="shrink-0 text-xs text-muted-foreground/70">
            {item.typeLabel}
          </span>
        )}
        {item.badge !== undefined && (
          <span className="text-xs text-muted-foreground">{item.badge}</span>
        )}
        {/*
          Only write access is called out. Almost every tool is read-only, so a
          READ badge on nearly every row repeated down the column without
          distinguishing anything, and set the loudest type on the row next to
          the disclosure chevron. Write is the one that can change something,
          so it is the one worth flagging.
        */}
        {accessLabel?.tone === 'write' && (
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-orange-400 dark:text-orange-500">
            WRITE
          </span>
        )}
        {hasDetail && (
          <button
            type="button"
            className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
            onClick={handleExpandToggle}
            tabIndex={-1}
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            <ChevronRight
              className={cn(
                'size-3.5 transition-transform duration-150',
                isExpanded && 'rotate-90'
              )}
            />
          </button>
        )}
      </div>
      {isExpanded && item.detail !== undefined && (
        <ItemDetailPanel
          detail={item.detail}
          indented={indented}
          onDismiss={onDismiss}
        />
      )}
    </div>
  );
};

// ─── Dispatch for any VisibleRow ─────────────────────────────────────

export const ResourcePickerRow: React.FC<{
  row: VisibleRow;
  /** Stable id for the row, so the focused control can point at it. */
  rowId?: string;
  cursorIndex: number;
  selection: ReadonlySet<string>;
  expandedItemId: string | undefined;
  multiSelect: boolean;
  onSelect: (item: ResourcePickerItem, event: React.MouseEvent) => void;
  onToggleSelection: (id: string) => void;
  onToggleDetail: (id: string) => void;
  onToggleProvider: (provider: string) => void;
  onExpandCategory: (category: ResourcePickerCategory) => void;
  onBrowseAll: () => void;
  onHover: (navIndex: number) => void;
  onDismiss?: () => void;
}> = ({
  row,
  rowId,
  cursorIndex,
  selection,
  expandedItemId,
  multiSelect,
  onSelect,
  onToggleSelection,
  onToggleDetail,
  onToggleProvider,
  onExpandCategory,
  onBrowseAll,
  onHover,
  onDismiss,
}) => {
  switch (row.kind) {
    case 'group-header':
      return (
        <ResourcePickerGroupHeader label={row.label} category={row.category} />
      );
    case 'browse-all':
      return (
        <ResourcePickerBrowseAllRow
          label={row.label}
          rowId={rowId}
          isCursored={row.navIndex === cursorIndex}
          onSelect={onBrowseAll}
          onHover={() => onHover(row.navIndex)}
        />
      );
    case 'show-more':
      return (
        <ResourcePickerShowMoreRow
          label={row.label}
          rowId={rowId}
          isCursored={row.navIndex === cursorIndex}
          onExpand={() => onExpandCategory(row.category)}
          onHover={() => onHover(row.navIndex)}
        />
      );
    case 'provider-trigger':
      return (
        <ResourcePickerProviderRow
          row={row}
          rowId={rowId}
          isCursored={row.navIndex === cursorIndex}
          onToggle={onToggleProvider}
          onHover={onHover}
        />
      );
    case 'item':
      return (
        <ResourcePickerItemRow
          item={row.item}
          rowId={rowId}
          isCursored={row.navIndex === cursorIndex}
          isSelected={selection.has(row.item.id)}
          indented={row.indented === true}
          showTypeLabel={row.showTypeLabel === true}
          forceExpanded={expandedItemId === row.item.id}
          multiSelect={multiSelect}
          onSelect={onSelect}
          onToggleSelection={onToggleSelection}
          onToggleDetail={onToggleDetail}
          onHover={onHover}
          onDismiss={onDismiss}
          navIndex={row.navIndex}
        />
      );
    default:
      return null;
  }
};
